import type { InventoryItem } from '../types'
import { EPC_HEX_LENGTH, classifyScanValue } from './scanClassify'
import { resolveEpcScan } from '../lib/services/rfidScanResolveService'

export type WedgeScanResult =
  | { kind: 'barcode'; value: string }
  | {
      kind: 'epc'
      value: string
      item: InventoryItem
      unitFound: boolean
      integrityAlarm: boolean
      message: string | null
    }
  | { kind: 'unknown'; value: string; message: string }
  | { kind: 'epc_error'; value: string; message: string }

/**
 * Classify a keyboard-wedge scan and, for UHF EPCs, resolve to a product.
 * Barcode values are returned unchanged for existing match logic.
 */
export async function resolveWedgeScan(
  adminId: string | null | undefined,
  raw: string
): Promise<WedgeScanResult> {
  const value = raw.trim()
  if (!value) {
    return {
      kind: 'unknown',
      value: '',
      message: 'Empty scan value',
    }
  }

  const kind = classifyScanValue(value)

  if (kind === 'barcode') {
    return { kind: 'barcode', value }
  }

  if (kind === 'unknown') {
    return {
      kind: 'unknown',
      value,
      message: `Not a ${EPC_HEX_LENGTH}-character hex EPC and not an 8–14 digit barcode. Value was not discarded.`,
    }
  }

  if (!adminId) {
    return {
      kind: 'epc_error',
      value,
      message: 'No active company context to resolve this EPC.',
    }
  }

  try {
    const result = await resolveEpcScan(adminId, value)

    if (!result.success || !result.item) {
      return {
        kind: 'epc_error',
        value,
        message: result.message || 'Could not resolve EPC to a product',
      }
    }

    return {
      kind: 'epc',
      value: result.item.barcode || result.item.sku || result.epcBase,
      item: result.item,
      unitFound: result.unitFound,
      integrityAlarm: result.integrityAlarm,
      message: result.message,
    }
  } catch (err) {
    return {
      kind: 'epc_error',
      value,
      message: err instanceof Error ? err.message : 'UHF resolve failed',
    }
  }
}
